import { DropDownItem } from "../../pojo/DropDownItem";
import type { CustomAttributes } from "../../pojo/CustomAttributes";
import type { EventCategory, EventRole, EventText } from "../../pojo/EventI";
import { EventService, type EventData } from "../../services/event.service";
import { S25Util } from "../../util/s25-util";
import { INGESTION_POINT_TYPE_ID } from "./data.ingestion.const";
import {
    CategoriesData,
    ContactRoleData,
    DataIngestionService,
    IngestionFieldDefinition,
    ObjectOccurrence,
    ObjectOccurrenceData,
    ResourceOccurrence,
} from "./data.ingestion.service";
import {
    findPrioritizedWorkflowSystemActionForItem,
    hydrateIngestionFieldFromCustomAttributeRows,
    parseFieldDefinitionIdFromWorkflowToken,
    stringFromMaybeNil,
} from "./ingestion.hydrate.util";
import type { IngestionRuntimeHydrateFn } from "./ingestion.runtime.hydrate.context";
import { Workflow } from "../workflow/service/Workflow";
import { WorkflowService } from "../workflow/service/workflow.service";
import { Item } from "../../pojo/Item";

const EVENT_EDIT_INCLUDES = ["categories", "attributes", "customers", "requirements", "text", "reservations"] as const;

function eventTextByTypeId(eventData: EventData, textTypeId: number): string {
    const rows = S25Util.array.forceArray(eventData.event_text ?? []) as EventText[];
    for (const row of rows) {
        if (Number(row.text_type_id) === textTypeId && row.text != null) {
            return String(row.text).trim();
        }
    }
    return "";
}

function primaryOrganizationId(eventData: EventData): number | undefined {
    const organizations = S25Util.array.forceArray(eventData.organization ?? []);
    for (const organization of organizations) {
        if (S25Util.toBool(organization.primary) && organization.organization_id) {
            return Number(organization.organization_id);
        }
    }
    const first = organizations[0];
    return first?.organization_id != null ? Number(first.organization_id) : undefined;
}

function eventCategoryItems(eventData: EventData): CategoriesData["items"] {
    const rawList = S25Util.array.forceArray(eventData.category) as EventCategory[];
    return rawList.map((row) => {
        const itemId = Number(row.category_id ?? 0);
        const itemName = String(row.category_name || (itemId ? `Category ${itemId}` : ""));
        return { itemId, itemName };
    });
}

function additionalOrganizationItems(eventData: EventData): DropDownItem[] {
    const primaryId = primaryOrganizationId(eventData);
    const organizations = S25Util.array.forceArray(eventData.organization ?? []);
    const items: DropDownItem[] = [];
    for (const organization of organizations) {
        const organizationId = Number(organization.organization_id ?? 0);
        if (!organizationId || organizationId === primaryId) {
            continue;
        }
        const itemName = String(organization.organization_name ?? "");
        items.push({
            itemId: organizationId,
            itemName,
            itemTypeId: Item.Ids.Organization,
            txt: itemName,
            val: String(organizationId),
        });
    }
    return items;
}

function requirementItems(eventData: EventData): CategoriesData["items"] {
    const rawList = S25Util.array.forceArray(eventData.requirement ?? []);
    return rawList.map((row) => {
        const itemId = Number(row.requirement_id ?? 0);
        const itemName = String(row.requirement_name || (itemId ? `Requirement ${itemId}` : ""));
        return { itemId, itemName };
    });
}

function buildObjectOccurrencePayloadFromEvent(eventData: EventData): ObjectOccurrenceData {
    const occurrences: ObjectOccurrence[] = [];
    const profiles = S25Util.array.forceArray(eventData.profile ?? []);
    for (const profile of profiles) {
        if (profile.prof_use != null && profile.prof_use !== 1) {
            continue;
        }
        const reservations = S25Util.array.forceArray(profile.reservation ?? []);
        for (const reservation of reservations) {
            const reservationId = reservation.reservation_id;
            if (reservationId == null) {
                continue;
            }
            const locationIds: number[] = [];
            for (const spaceReservation of S25Util.array.forceArray(reservation.space_reservation ?? [])) {
                const spaceId = spaceReservation.space_id;
                if (spaceId != null) {
                    locationIds.push(Number(spaceId));
                }
            }
            const resources: ResourceOccurrence[] = [];
            for (const resourceReservation of S25Util.array.forceArray(reservation.resource_reservation ?? [])) {
                const resourceId = resourceReservation.resource_id;
                if (resourceId == null) {
                    continue;
                }
                resources.push({
                    resourceId: Number(resourceId),
                    actRsrcCount: resourceReservation.quantity ?? 1,
                });
            }
            occurrences.push({
                occurrenceUuid: String(reservationId),
                startDate: reservation.reservation_start_dt ?? reservation.event_start_dt ?? "",
                endDate: reservation.reservation_end_dt ?? reservation.event_end_dt ?? "",
                locationIds,
                resources,
                comment: reservation.rsrv_comments,
            });
        }
    }
    return { occurrences };
}

function buildEventContactRolePayload(eventData: EventData): ContactRoleData {
    const items: ContactRoleData["items"] = [];
    const roles = S25Util.array.forceArray(eventData.role ?? []) as EventRole[];
    for (const roleRow of roles) {
        const contactId = roleRow.contact?.contact_id;
        if (!contactId) {
            continue;
        }
        const roleId = roleRow.role_id;
        if (!roleId) {
            continue;
        }
        items.push({
            roleId: roleId,
            roleName: roleRow.role_name,
            contactId,
            contactName: roleRow.contact?.contact_name,
        });
    }
    return { items };
}

async function enrichEventContactRoleNames(
    fieldDef: IngestionFieldDefinition,
    payload: ContactRoleData,
): Promise<void> {
    if (!payload.items.length) {
        fieldDef.jsonFieldItem = { items: [] };
        return;
    }
    const roleIds = [...new Set(payload.items.map((item) => item.roleId))];
    const contactIds = [...new Set(payload.items.map((item) => item.contactId))];
    const [roleNames, contactNames] = await Promise.all([
        DataIngestionService.getObjectNames("ROLE", roleIds),
        DataIngestionService.getObjectNames("CONTACT", contactIds),
    ]);
    fieldDef.jsonFieldItem = {
        items: payload.items.map((item) => ({
            ...item,
            roleName: roleNames[`${item.roleId}-ROLE`] || item.roleName,
            contactName: contactNames[`${item.contactId}-CONTACT`] || item.contactName,
        })),
    };
}

function expectedHeadcountFromEvent(eventData: EventData): number | undefined {
    const profiles = S25Util.array.forceArray(eventData.profile ?? []);
    for (const profile of profiles) {
        if (profile.prof_use != null && profile.prof_use !== 1) {
            continue;
        }
        const count = profile.expected_count ?? eventData.exp_head_count;
        if (count != null && String(count).trim() !== "") {
            const numeric = Number(count);
            if (!Number.isNaN(numeric)) {
                return numeric;
            }
        }
    }
    return undefined;
}

function profileDurationIso(eventData: EventData, key: "pre_event" | "post_event"): string | undefined {
    const profiles = S25Util.array.forceArray(eventData.profile ?? []);
    let profile = profiles[0];
    if (profiles.length > 1) {
        profile = profiles.find((p) => p.prof_use === 1) ?? profile;
    }
    const value = profile?.[key];
    return value != null && String(value).trim() !== "" ? String(value) : undefined;
}

function setupTakedownDurationIso(eventData: EventData, isSetup: boolean): string | undefined {
    const profiles = S25Util.array.forceArray(eventData.profile ?? []);
    let profile = profiles[0];
    if (profiles.length > 1) {
        profile = profiles.find((p) => p.prof_use === 1) ?? profile;
    }
    if (isSetup) {
        return profile?.setup_profile?.setup_tm;
    }
    return profile?.takedown_profile?.tdown_tm;
}

function setScalarOnField(fieldDef: IngestionFieldDefinition, value: string | number | undefined): boolean {
    if (value === undefined) {
        return false;
    }
    const stringValue = typeof value === "number" ? String(value) : value;
    let itemTypeId: Item.Ids | null = null;
    let itemName = "";
    switch (fieldDef.fieldType) {
        case "ORGANIZATION":
            itemTypeId = Item.Ids.Organization;
            itemName = "";
            break;
    }

    if (itemTypeId !== null) {
        const numeric = typeof value === "number" ? value : parseInt(stringValue, 10);
        if (Number.isNaN(numeric)) {
            return false;
        }
        fieldDef.longAnswer = String(numeric);
        fieldDef.specialFieldItem = {
            itemId: numeric,
            itemName: itemName,
            itemTypeId: itemTypeId,
            txt: itemName,
            val: String(numeric),
        };
        return true;
    }

    fieldDef.longAnswer = stringValue;
    return true;
}

function extractValueForEventWorkflowInput(inputCode: string, eventData: EventData): string | number | undefined {
    switch (inputCode) {
        case "eventIdField":
            return eventData.event_id;
        case "eventName":
            return eventData.event_name;
        case "startDate":
            return eventData.start_date;
        case "endDate":
            return eventData.end_date;
        case "primaryOrgId":
            return primaryOrganizationId(eventData);
        case "eventTitle":
            return stringFromMaybeNil(eventData.event_title);
        case "eventDescription":
            return eventTextByTypeId(eventData, 1);
        case "internalNotes":
            return eventTextByTypeId(eventData, 2);
        case "confirmationText":
            return eventTextByTypeId(eventData, 3);
        case "eventComment":
            return eventData.event_comment;
        case "expectedHeadcount":
            return expectedHeadcountFromEvent(eventData);
        case "preEventDuration":
            return profileDurationIso(eventData, "pre_event");
        case "postEventDuration":
            return profileDurationIso(eventData, "post_event");
        case "setupDuration":
        case "takedownDuration":
            return setupTakedownDurationIso(eventData, inputCode === "setupDuration");
        case "enableRegistration":
        case "registrationFormId":
        case "registrationStart":
        case "registrationEnd":
        case "registrants":
        case "sendSurvey":
        case "surveyFormId":
        case "surveyOffsetDays":
        case "useAssignmentPolicy":
        case "categoryIds":
        case "additionalOrgIds":
        case "requirementIds":
            return undefined;
        default:
            return undefined;
    }
}

function applyWorkflowInputHydration(
    fieldDefinitions: IngestionFieldDefinition[],
    workflowAction: Workflow.WorkflowAction | undefined,
    eventData: EventData,
): boolean {
    if (!workflowAction?.inputs?.length) {
        return false;
    }
    let changed = false;
    const byFieldId = new Map<number, IngestionFieldDefinition>();
    for (const fieldDef of fieldDefinitions) {
        if (fieldDef.fieldDefinitionId != null) {
            byFieldId.set(fieldDef.fieldDefinitionId, fieldDef);
        }
    }
    for (const workflowInput of workflowAction.inputs) {
        const fieldDefinitionId = parseFieldDefinitionIdFromWorkflowToken(workflowInput.inputValue);
        if (fieldDefinitionId == null) {
            continue;
        }
        const fieldDef = byFieldId.get(fieldDefinitionId);
        if (!fieldDef || fieldDef.fieldType === "LIVE_25_EVENT_FORM") {
            continue;
        }
        const extracted = extractValueForEventWorkflowInput(workflowInput.inputCode, eventData);
        if (extracted === undefined || extracted === null || String(extracted).trim() === "") {
            continue;
        }
        if (setScalarOnField(fieldDef, extracted)) {
            changed = true;
        }
    }
    return changed;
}

async function hydrateParticipatingTypedFields(
    fieldDefinitions: IngestionFieldDefinition[],
    eventData: EventData,
    eventId: number,
): Promise<boolean> {
    let changed = false;
    const attributeRows = S25Util.array.forceArray(eventData.custom_attribute) as CustomAttributes.DataI[];

    for (const fieldDef of fieldDefinitions) {
        if (fieldDef.fieldType === "LIVE_25_EVENT_FORM") {
            fieldDef.longAnswer = String(eventId);
            changed = true;
        } else if (fieldDef.fieldType === "CATEGORIES") {
            fieldDef.jsonFieldItem = { items: eventCategoryItems(eventData) } as CategoriesData;
            changed = true;
        } else if (fieldDef.fieldType === "ORGANIZATION_MULTISELECT") {
            fieldDef.jsonFieldItem = { items: additionalOrganizationItems(eventData) } as CategoriesData;
            changed = true;
        } else if (fieldDef.fieldType === "REQUIREMENTS") {
            fieldDef.jsonFieldItem = { items: requirementItems(eventData) } as CategoriesData;
            changed = true;
        } else if (fieldDef.fieldType === "CONTACT_ROLE") {
            const payload = buildEventContactRolePayload(eventData);
            await enrichEventContactRoleNames(fieldDef, payload);
            changed = true;
        } else if (fieldDef.fieldType === "OBJECT_OCCURRENCE") {
            fieldDef.jsonFieldItem = buildObjectOccurrencePayloadFromEvent(eventData);
            changed = true;
        } else if (hydrateIngestionFieldFromCustomAttributeRows(fieldDef, attributeRows, ["CUSTOM_ATTRIBUTE"])) {
            changed = true;
        } else if (fieldDef.fieldType === "EVENT_TYPE" && eventData.event_type_id != null) {
            const typeId = Number(eventData.event_type_id);
            fieldDef.longAnswer = String(typeId);
            fieldDef.specialFieldItem = {
                itemId: typeId,
                itemName: eventData.event_type_name ?? "",
                itemTypeId: Item.Ids.EventType,
                txt: eventData.event_type_name ?? "",
                val: String(typeId),
            };
            changed = true;
        }
    }
    return changed;
}

/**
 * Hydrates ingestion answers from the current event row for Connect25 event-edit forms.
 * Also applies scalar mappings declared on the workflow `EDIT_EVENT` (or `CREATE_EVENT`) action.
 * `LIVE_25_EVENT_FORM` only receives the event id in `longAnswer`; the embedded 25Live form hydrates itself.
 *
 * Event API datetimes include institution timezone offsets (e.g. `2026-06-29T17:30:00-07:00`).
 * We strip them on load so ingestion fields and workflow EDIT_EVENT see offset-free local datetimes.
 */
export function createEventEditHydrate(eventId: number): IngestionRuntimeHydrateFn {
    return async (context): Promise<boolean> => {
        const eventData = await EventService.getEventInclude(eventId, [...EVENT_EDIT_INCLUDES], null, true);
        if (!eventData?.event_id) {
            return false;
        }

        const submissionSnapshot = context.submission();
        const workflowProcessId =
            submissionSnapshot?.workflowProcessId ??
            context.ingestionPoint?.workflowId ??
            context.ingestionPoint?.defaultWorkflowId;

        let workflowProcess: Workflow.WorkflowProcess | undefined;
        if (workflowProcessId && !Number.isNaN(workflowProcessId)) {
            try {
                workflowProcess = await WorkflowService.getWorkflowProcess(workflowProcessId);
            } catch {
                workflowProcess = undefined;
            }
        }

        const ingestionPointId = context.ingestionPoint?.ingestionPointId;
        const workflowAction =
            ingestionPointId != null
                ? findPrioritizedWorkflowSystemActionForItem(
                      workflowProcess,
                      INGESTION_POINT_TYPE_ID,
                      Number(ingestionPointId),
                      ["EDIT_EVENT", "CREATE_EVENT"],
                  )
                : undefined;

        const typedChanged = await hydrateParticipatingTypedFields(context.fieldDefinitions, eventData, eventId);
        const mappedChanged = applyWorkflowInputHydration(context.fieldDefinitions, workflowAction, eventData);

        return typedChanged || mappedChanged;
    };
}
